// The Sparkle kit — reusable primitives that compose every Sparkle screen.
// Faithful to the design profile + prototype (sparkle-mode.jsx). Pure
// presentation; data comes from the app's existing hooks. Only rendered when
// the active theme is "sparkle" (callers gate with useIsSparkle).
import type { CSSProperties, ReactNode } from "react";
import { RAINBOW } from "./glyphs";

const INK = "#2A0E5C";
const VIOLET = "#6C2BD9";
const MUTE = "#7B5E97";

/** White die-cut sticker card: 3px white edge, colored glow, optional tilt. */
export function Sticker({
  children,
  glow = "#FF2D9B",
  rotate = 0,
  radius = 20,
  pad = "16px 18px",
  className,
  style,
}: {
  children: ReactNode;
  glow?: string;
  rotate?: number;
  radius?: number;
  pad?: string;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      className={className}
      style={{
        background: "#fff",
        border: "3px solid #fff",
        borderRadius: radius,
        padding: pad,
        boxShadow: `0 0 0 2px ${glow}55, 0 8px 0 -2px ${glow}33, 0 14px 26px -10px ${glow}88`,
        transform: rotate ? `rotate(${rotate}deg)` : undefined,
        position: "relative",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** Iridescent conic-gradient ring wrapper (e.g. behind the Add bar). */
export function Holo({
  children,
  radius = 18,
  style,
}: {
  children: ReactNode;
  radius?: number;
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        background: `conic-gradient(from 210deg, ${RAINBOW.join(",")}, ${RAINBOW[0]})`,
        borderRadius: radius,
        padding: 3,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** Stat sticker: gradient icon disc + Lilita number + eyebrow. Optional click. */
export function MetricBlob({
  label,
  value,
  color,
  icon,
  sub,
  rotate = 0,
  active = false,
  onClick,
}: {
  label: string;
  value: ReactNode;
  color: string;
  icon?: ReactNode;
  sub?: ReactNode;
  rotate?: number;
  active?: boolean;
  onClick?: () => void;
}) {
  const interactive = !!onClick;
  return (
    <Sticker
      glow={color}
      radius={20}
      pad="13px 10px 12px"
      style={{
        textAlign: "center",
        cursor: interactive ? "pointer" : undefined,
        transition: "transform .12s ease, box-shadow .12s ease",
        transform: `rotate(${rotate}deg) translateY(${active ? -2 : 0}px)`,
      }}
    >
      <div
        onClick={onClick}
        role={interactive ? "button" : undefined}
        tabIndex={interactive ? 0 : undefined}
      >
        {icon ? (
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 999,
              margin: "0 auto",
              display: "grid",
              placeItems: "center",
              background: `linear-gradient(140deg, ${color}, ${color}cc)`,
              boxShadow: `0 3px 0 ${color}66`,
              border: "2px solid #fff",
            }}
          >
            {icon}
          </div>
        ) : null}
        <div style={{ fontFamily: '"Lilita One", cursive', fontSize: 26, lineHeight: 1, marginTop: icon ? 7 : 0, color: INK }}>
          {value}
        </div>
        <div
          style={{
            fontFamily: '"Fredoka", sans-serif',
            fontSize: 9.5,
            fontWeight: 600,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: MUTE,
            marginTop: 3,
          }}
        >
          {label}
        </div>
        {sub ? <div style={{ fontSize: 11, color: MUTE, marginTop: 2 }}>{sub}</div> : null}
      </div>
    </Sticker>
  );
}

/** Filter/segmented pill — filled + chunky shadow when active. */
export function NeonPill({
  children,
  color = "#FF2D9B",
  active = false,
  onClick,
}: {
  children: ReactNode;
  color?: string;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        fontFamily: '"Fredoka", sans-serif',
        fontWeight: 600,
        fontSize: 12.5,
        padding: "5px 14px",
        borderRadius: 999,
        border: `2px solid ${color}`,
        color: active ? "#fff" : color,
        background: active ? color : "#fff",
        boxShadow: active ? `0 3px 0 ${color}66` : "none",
        cursor: "pointer",
        transition: "all .12s ease",
      }}
    >
      {children}
    </button>
  );
}

const STATUS: Record<string, { c: string; label: string }> = {
  idea: { c: "#9B2BFF", label: "Idea" },
  drafting: { c: "#FF861E", label: "Drafting" },
  scheduled: { c: "#00C6D6", label: "Scheduled" },
  published: { c: "#10C99B", label: "Published" },
};

/** Tonal status pill: 1.5px {c} border, {c}1f fill, {c} text. */
export function StatusBadge({ status, onClick }: { status: string; onClick?: () => void }) {
  const s = STATUS[status] ?? { c: MUTE, label: status };
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        fontFamily: '"Fredoka", sans-serif',
        fontWeight: 600,
        fontSize: 11.5,
        padding: "3px 10px",
        borderRadius: 999,
        border: `1.5px solid ${s.c}`,
        color: s.c,
        background: `${s.c}1f`,
        cursor: onClick ? "pointer" : "default",
        transition: "all .12s ease",
      }}
    >
      {s.label}
    </button>
  );
}

/** Eyebrow + rainbow-clipped Lilita wordmark with the double drop-shadow. */
export function ScreenTitle({
  eyebrow,
  title,
  sub,
  star,
}: {
  eyebrow?: string;
  title: string;
  sub?: ReactNode;
  star?: ReactNode;
}) {
  return (
    <div>
      {eyebrow ? (
        <div
          style={{
            fontFamily: '"Fredoka", sans-serif',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "#9B2BFF",
          }}
        >
          {eyebrow}
        </div>
      ) : null}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: eyebrow ? 4 : 0 }}>
        <h1
          style={{
            margin: 0,
            fontFamily: '"Lilita One", cursive',
            fontSize: 40,
            lineHeight: 0.95,
            letterSpacing: "0.5px",
            background: `linear-gradient(92deg, ${RAINBOW.join(",")})`,
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
            filter: `drop-shadow(2px 2px 0 #fff) drop-shadow(3px 3px 0 ${VIOLET}55)`,
          }}
        >
          {title}
        </h1>
        {star}
      </div>
      {sub ? <div style={{ fontSize: 13.5, color: VIOLET, fontWeight: 500, marginTop: 4 }}>{sub}</div> : null}
    </div>
  );
}
